import { ArrowLeft, ArrowRight, Check, Droplets, Ruler, ShieldCheck, SunMedium, Wind } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { BalconyProfile, Care, Light } from "../../data/model";
import { budgetProfile, noFitProfile, standardProfile } from "../../data/scenarios";
import { careLabels, lightLabels, styles } from "../../data/taxonomy";
import { areaM2 } from "../../lib/calculations";
import { formatArea, formatCompactMoney } from "../../lib/format";
import { Notice } from "../../components/Notice";

interface Props {
  profile: BalconyProfile;
  onChange: (profile: BalconyProfile) => void;
  onGenerate: () => void;
  hasSaved: boolean;
  onRestore: () => void;
}

const steps = [
  ["Kích thước", "Phần sàn có thể dùng"],
  ["Nắng & gió", "Vi khí hậu"],
  ["Ngân sách", "Khoảng đầu tư"],
  ["Nếp xanh", "Gu và nhịp chăm"],
  ["An toàn", "Ràng buộc gia đình"],
];

export function ProfileWizard({ profile, onChange, onGenerate, hasSaved, onRestore }: Props) {
  const [step, setStep] = useState(0);
  const [showScenarios, setShowScenarios] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const errorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (errors.length) errorRef.current?.focus();
  }, [errors]);

  const patch = <K extends keyof BalconyProfile>(key: K, value: BalconyProfile[K]) =>
    onChange({ ...profile, [key]: value });

  const stepErrors = () => {
    const next: string[] = [];
    if (step === 0) {
      if (profile.widthCm < 100 || profile.widthCm > 800) next.push("Nhập chiều rộng từ 100 đến 800 cm.");
      if (profile.depthCm < 50 || profile.depthCm > 400) next.push("Nhập chiều sâu từ 50 đến 400 cm.");
    }
    if (step === 2 && (profile.budget < 3000000 || profile.budget > 80000000)) {
      next.push("Chọn ngân sách từ 3 đến 80 triệu đồng.");
    }
    return next;
  };

  const next = () => {
    const found = stepErrors();
    setErrors(found);
    if (!found.length) setStep((current) => Math.min(current + 1, steps.length - 1));
  };

  const generate = () => {
    const allErrors: string[] = [];
    if (profile.widthCm < 100 || profile.widthCm > 800) allErrors.push("Nhập chiều rộng từ 100 đến 800 cm.");
    if (profile.depthCm < 50 || profile.depthCm > 400) allErrors.push("Nhập chiều sâu từ 50 đến 400 cm.");
    if (profile.budget < 3000000 || profile.budget > 80000000) allErrors.push("Chọn ngân sách từ 3 đến 80 triệu đồng.");
    setErrors(allErrors);
    if (!allErrors.length) onGenerate();
  };

  const loadScenario = (nextProfile: BalconyProfile) => {
    onChange({ ...nextProfile });
    setShowScenarios(false);
    setErrors([]);
  };

  return (
    <div className="designer-start">
      <div className="designer-intro">
        <div>
          <span className="eyebrow">Xưởng ban công · công cụ mô phỏng</span>
          <h1>Kể cho chúng tôi nơi khoảng xanh sẽ sống.</h1>
          <p>5 nhóm điều kiện, khoảng 3 phút. Không cần ảnh và không gửi dữ liệu ra ngoài.</p>
        </div>
        <button className="scenario-toggle" type="button" aria-expanded={showScenarios} onClick={() => setShowScenarios((value) => !value)}>
          Dùng trạng thái demo
        </button>
        {showScenarios && (
          <div className="scenario-menu">
            <button type="button" onClick={() => loadScenario(standardProfile)}><strong>Tiêu chuẩn</strong><span>3,2 × 1,45 m · 18 triệu</span></button>
            <button type="button" onClick={() => loadScenario(noFitProfile)}><strong>Không phù hợp</strong><span>1,2 × 0,6 m</span></button>
            <button type="button" onClick={() => loadScenario(budgetProfile)}><strong>Vượt ngân sách</strong><span>2,8 × 1,2 m · 9 triệu</span></button>
          </div>
        )}
      </div>

      {hasSaved && (
        <Notice
          tone="success"
          title="Có một bản phác đã cất trên thiết bị."
          actions={<button className="text-button" type="button" onClick={onRestore}>Mở bản đã cất</button>}
        >
          <p>Bạn có thể tiếp tục bản cũ hoặc tạo một phương án mới bên dưới.</p>
        </Notice>
      )}

      <div className="wizard-progress" aria-label={`Bước ${step + 1} trên ${steps.length}`}>
        <div className="progress-line" aria-hidden="true"><i style={{ width: `${(step / (steps.length - 1)) * 100}%` }} /></div>
        {steps.map(([title], index) => (
          <button
            type="button"
            key={title}
            className={index === step ? "active" : index < step ? "done" : ""}
            onClick={() => index < step && setStep(index)}
            disabled={index > step}
            aria-current={index === step ? "step" : undefined}
          >
            <span>{index < step ? <Check aria-hidden="true" /> : index + 1}</span>
            <small>{title}</small>
          </button>
        ))}
      </div>

      {errors.length > 0 && (
        <div className="error-summary" ref={errorRef} tabIndex={-1} role="alert">
          <strong>Còn vài chỗ cần bạn kiểm tra.</strong>
          <ul>{errors.map((error) => <li key={error}>{error}</li>)}</ul>
        </div>
      )}

      <div className="wizard-shell">
        <section className="wizard-card" aria-labelledby="wizard-step-title">
          <div className="wizard-step-heading">
            <span>0{step + 1}</span>
            <div><p>{steps[step][1]}</p><h2 id="wizard-step-title">{wizardTitle(step)}</h2></div>
          </div>

          {step === 0 && (
            <div className="wizard-fields">
              <div className="dimension-grid">
                <label>
                  <span>Chiều rộng (cm)</span>
                  <input
                    type="number"
                    min="100"
                    max="800"
                    value={profile.widthCm}
                    onChange={(event) => patch("widthCm", Number(event.target.value))}
                    aria-invalid={errors.some((error) => error.includes("rộng"))}
                  />
                  <small>Đo theo mặt lan can.</small>
                </label>
                <span className="dimension-x" aria-hidden="true">×</span>
                <label>
                  <span>Chiều sâu (cm)</span>
                  <input
                    type="number"
                    min="50"
                    max="400"
                    value={profile.depthCm}
                    onChange={(event) => patch("depthCm", Number(event.target.value))}
                    aria-invalid={errors.some((error) => error.includes("sâu"))}
                  />
                  <small>Từ cửa đến lan can.</small>
                </label>
              </div>
              <fieldset>
                <legend>Cửa ban công mở thế nào?</legend>
                <div className="segmented">
                  {[
                    ["inward", "Mở vào nhà"],
                    ["sliding", "Cửa trượt"],
                    ["outward", "Mở ra ban công"],
                  ].map(([value, label]) => (
                    <label key={value}><input type="radio" name="door" checked={profile.doorType === value} onChange={() => patch("doorType", value as BalconyProfile["doorType"])} /><span>{label}</span></label>
                  ))}
                </div>
              </fieldset>
            </div>
          )}

          {step === 1 && (
            <div className="wizard-fields">
              <fieldset>
                <legend>Ban công nhìn về hướng nào?</legend>
                <div className="direction-grid">
                  {(["Đông", "Tây", "Nam", "Bắc", "Đông Nam", "Đông Bắc", "Tây Nam", "Tây Bắc"] as const).map((direction) => (
                    <label key={direction}><input type="radio" name="direction" checked={profile.direction === direction} onChange={() => patch("direction", direction)} /><span>{direction}</span></label>
                  ))}
                </div>
              </fieldset>
              <fieldset>
                <legend>Nắng trực tiếp mỗi ngày?</legend>
                <div className="segmented four">
                  {(["0–1 giờ", "2–4 giờ", "5–7 giờ", "Trên 7 giờ"] as const).map((hours) => (
                    <label key={hours}><input type="radio" name="sun-hours" checked={profile.directSunHours === hours} onChange={() => {
                      const mapped: Record<typeof hours, Light> = { "0–1 giờ": "shade", "2–4 giờ": "morning", "5–7 giờ": "partial", "Trên 7 giờ": "harsh" };
                      onChange({ ...profile, directSunHours: hours, light: mapped[hours] });
                    }} /><span>{hours}</span></label>
                  ))}
                </div>
              </fieldset>
              <div className="two-fieldsets">
                <fieldset>
                  <legend>Mức gió thường gặp?</legend>
                  <select value={profile.wind} onChange={(event) => patch("wind", event.target.value as BalconyProfile["wind"])}>
                    <option value="sheltered">Kín gió</option>
                    <option value="normal">Gió vừa</option>
                    <option value="windy">Lộng gió</option>
                  </select>
                </fieldset>
                <fieldset>
                  <legend>Nguồn nước?</legend>
                  <select value={profile.waterAccess} onChange={(event) => patch("waterAccess", event.target.value as BalconyProfile["waterAccess"])}>
                    <option value="tap">Vòi tại ban công</option>
                    <option value="near">Cách dưới 5 m</option>
                    <option value="far">Phải mang từ xa</option>
                  </select>
                </fieldset>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="wizard-fields budget-fields">
              <label className="budget-range">
                <span>Khoảng bạn muốn đầu tư</span>
                <strong>{formatCompactMoney(profile.budget)}</strong>
                <input type="range" min="3000000" max="80000000" step="1000000" value={profile.budget} onChange={(event) => patch("budget", Number(event.target.value))} />
                <small><span>3 triệu</span><span>80 triệu</span></small>
              </label>
              <div className="budget-presets">
                {[9000000, 18000000, 30000000].map((amount) => (
                  <button type="button" className={profile.budget === amount ? "active" : ""} key={amount} onClick={() => patch("budget", amount)}>{formatCompactMoney(amount)}</button>
                ))}
              </div>
              <Notice tone="info" title="Công cụ sẽ không tự giấu hạng mục để vừa giá.">
                <p>Nếu phương án vượt ngân sách, bạn vẫn thấy bản đầy đủ cùng cách hạ chi phí.</p>
              </Notice>
            </div>
          )}

          {step === 3 && (
            <div className="wizard-fields">
              <fieldset>
                <legend>Bạn muốn ban công mang cảm giác nào?</legend>
                <div className="style-choice-grid">
                  {styles.map((style) => (
                    <label key={style.id} className={profile.styleId === style.id ? "active" : ""}>
                      <input type="radio" name="style" checked={profile.styleId === style.id} onChange={() => patch("styleId", style.id)} />
                      <img src={style.image} width="1448" height="1086" alt="" />
                      <span><strong>{style.shortName}</strong><small>{style.ideal}</small></span>
                    </label>
                  ))}
                </div>
              </fieldset>
              <fieldset>
                <legend>Mỗi tuần bạn muốn chăm bao lâu?</legend>
                <div className="care-choice">
                  {(Object.keys(careLabels) as Care[]).map((key) => (
                    <label key={key}><input type="radio" name="care" checked={profile.care === key} onChange={() => patch("care", key)} /><span><strong>{careLabels[key]}</strong><small>{key === "light" ? "Kiểm tra và tưới gọn" : key === "medium" ? "Có tỉa/lau lá định kỳ" : "Thích thu hái và chăm kỹ"}</small></span></label>
                  ))}
                </div>
              </fieldset>
            </div>
          )}

          {step === 4 && (
            <div className="wizard-fields">
              <div className="constraint-grid">
                <label className={profile.hasKids ? "active" : ""}>
                  <input type="checkbox" checked={profile.hasKids} onChange={(event) => patch("hasKids", event.target.checked)} />
                  <ShieldCheck aria-hidden="true" />
                  <span><strong>Trẻ dưới 6 tuổi</strong><small>Tránh gai, nhựa kích ứng và chậu treo thấp.</small></span>
                </label>
                <label className={profile.hasPets ? "active" : ""}>
                  <input type="checkbox" checked={profile.hasPets} onChange={(event) => patch("hasPets", event.target.checked)} />
                  <ShieldCheck aria-hidden="true" />
                  <span><strong>Chó hoặc mèo</strong><small>Chỉ dùng cây có nhãn pet-safe trong recommendation.</small></span>
                </label>
              </div>
              <div className="profile-review">
                <h3>Rêu Nắng sẽ đối chiếu</h3>
                <ul>
                  <li><Ruler aria-hidden="true" /><span><strong>{profile.widthCm} × {profile.depthCm} cm</strong>{formatArea(areaM2(profile.widthCm, profile.depthCm))} m² · cửa {profile.doorType === "sliding" ? "trượt" : profile.doorType === "inward" ? "mở vào" : "mở ra"}</span></li>
                  <li><SunMedium aria-hidden="true" /><span><strong>{profile.direction} · {lightLabels[profile.light]}</strong>{profile.directSunHours} nắng trực tiếp</span></li>
                  <li><Wind aria-hidden="true" /><span><strong>{profile.wind === "windy" ? "Lộng gió" : profile.wind === "normal" ? "Gió vừa" : "Kín gió"}</strong>lọc vật thể phù hợp</span></li>
                  <li><Droplets aria-hidden="true" /><span><strong>{profile.waterAccess === "tap" ? "Có vòi tại chỗ" : profile.waterAccess === "near" ? "Nguồn nước gần" : "Nguồn nước xa"}</strong>{careLabels[profile.care]}</span></li>
                </ul>
              </div>
              <Notice tone="warning" title="Phương án không thay thế khảo sát thực tế.">
                <p>Cần kiểm tra tải trọng, chống thấm, thoát nước, nguồn điện và nội quy tòa nhà trước thi công.</p>
              </Notice>
            </div>
          )}

          <div className="wizard-actions">
            <button className="button button-ghost" type="button" disabled={step === 0} onClick={() => { setErrors([]); setStep((current) => current - 1); }}>
              <ArrowLeft aria-hidden="true" /> Quay lại
            </button>
            {step < steps.length - 1 ? (
              <button className="button button-primary" type="button" onClick={next}>Tiếp tục <ArrowRight aria-hidden="true" /></button>
            ) : (
              <button className="button button-primary" type="button" onClick={generate}>Tạo phương án mô phỏng <ArrowRight aria-hidden="true" /></button>
            )}
          </div>
        </section>
        <aside className="wizard-preview" aria-label="Tóm tắt điều kiện ban công">
          <div className="mini-balcony" style={{ aspectRatio: `${Math.max(profile.widthCm, 100)} / ${Math.max(profile.depthCm, 50)}` }}>
            <span className={`sun-zone sun-${profile.light}`} />
            <i className="door-mark" />
            <span className="measure measure-width">{profile.widthCm} cm</span>
            <span className="measure measure-depth">{profile.depthCm} cm</span>
            <strong>{formatArea(areaM2(profile.widthCm, profile.depthCm))} m²</strong>
          </div>
          <dl>
            <div><dt>Nắng</dt><dd>{lightLabels[profile.light]}</dd></div>
            <div><dt>Ngân sách</dt><dd>{formatCompactMoney(profile.budget)}</dd></div>
            <div><dt>Phong cách</dt><dd>{styles.find((style) => style.id === profile.styleId)?.shortName}</dd></div>
          </dl>
          <small>Preview diện tích tương đối, không phải bản vẽ kỹ thuật.</small>
        </aside>
      </div>
    </div>
  );
}

const wizardTitle = (step: number) =>
  [
    "Ban công rộng bao nhiêu?",
    "Nắng và gió đi qua đây thế nào?",
    "Bạn muốn dành bao nhiêu cho góc này?",
    "Bạn muốn sống cùng ban công ra sao?",
    "Trong nhà có ai cần được ưu tiên?",
  ][step];
